import { useEffect, useState } from "react";
import { apiUrl, authFetch } from "../../utils/api";
import { loadPdfHeaderData } from "../../utils/pdfHeader";
import { applyPdfHeaderFooterToBlob } from "../../utils/pdfStamp";
import { showGlobalToast } from "../../utils/globalNotify";
import { getPendingQmExport, clearPendingQmExport } from "../../utils/qmExport";
import { Btn } from "../ui";

type Phase = "idle" | "processing" | "ready" | "failed";

// App-weit gemountet: beobachtet den laufenden QM-Handbuch-Export (Zustand in
// localStorage, Status im Backend) und zeigt bei Fertigstellung ein Popup mit
// Download-Button — unabhaengig davon, auf welcher Seite der Nutzer gerade ist.
export function QmExportWatcher() {
  const [phase, setPhase] = useState<Phase>(() => (getPendingQmExport() ? "processing" : "idle"));
  const [downloading, setDownloading] = useState(false);

  // Auf Start/Ende eines Exports reagieren (Event aus setPendingQmExport/clear).
  useEffect(() => {
    const onChange = () => setPhase(getPendingQmExport() ? "processing" : "idle");
    window.addEventListener("qm-export:changed", onChange);
    return () => window.removeEventListener("qm-export:changed", onChange);
  }, []);

  // Solange ein Export laeuft, den Status pollen.
  useEffect(() => {
    if (phase !== "processing") return;
    let cancelled = false;

    const poll = async () => {
      const pending = getPendingQmExport();
      if (!pending) { setPhase("idle"); return; }
      try {
        const resp = await authFetch(
          apiUrl(`/api/v1/businesses/${pending.businessId}/qm_handbook_exports/${pending.exportId}`)
        );
        if (resp.status === 404) { clearPendingQmExport(); return; }
        if (!resp.ok) return;
        const state = (await resp.json()) as { status: string; download_ready: boolean };
        if (cancelled) return;
        if (state.status === "failed") setPhase("failed");
        else if (state.download_ready) setPhase("ready");
      } catch {
        /* Netzwerkfehler – beim naechsten Intervall erneut versuchen */
      }
    };

    void poll();
    const id = setInterval(() => void poll(), 4000);
    return () => { cancelled = true; clearInterval(id); };
  }, [phase]);

  const download = async () => {
    const pending = getPendingQmExport();
    if (!pending) { setPhase("idle"); return; }
    setDownloading(true);
    try {
      const resp = await authFetch(
        apiUrl(`/api/v1/businesses/${pending.businessId}/qm_handbook_exports/${pending.exportId}/download`)
      );
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const headerData = await loadPdfHeaderData(pending.businessId);
      const stamped = await applyPdfHeaderFooterToBlob(blob, headerData);
      const url = window.URL.createObjectURL(stamped);
      const link = document.createElement("a");
      link.href = url;
      link.download = `qm-handbuch-${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      clearPendingQmExport();
      setPhase("idle");
    } catch {
      showGlobalToast("Download fehlgeschlagen. Bitte erneut versuchen.", "error");
    } finally {
      setDownloading(false);
    }
  };

  const dismiss = () => { clearPendingQmExport(); setPhase("idle"); };

  // Waehrend der Verarbeitung kein Popup (nur der Start-Toast); erst bei Ergebnis.
  if (phase === "idle" || phase === "processing") return null;

  return (
    <div className="fixed bottom-4 right-4 z-50 max-w-sm rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-lg">
      {phase === "ready" ? (
        <>
          <div className="font-semibold text-slate-900">QM-Handbuch-Export ist fertig</div>
          <div className="mt-1 text-sm text-slate-600">Das PDF wurde im Hintergrund erstellt.</div>
          <div className="mt-3 flex gap-2">
            <Btn onClick={() => void download()} disabled={downloading}>
              {downloading ? "Lädt …" : "Herunterladen"}
            </Btn>
            <Btn variant="secondary" onClick={dismiss} disabled={downloading}>Schließen</Btn>
          </div>
        </>
      ) : (
        <>
          <div className="font-semibold text-red-700">QM-Handbuch-Export fehlgeschlagen</div>
          <div className="mt-1 text-sm text-slate-600">Bitte starten Sie den Export erneut.</div>
          <div className="mt-3">
            <Btn variant="secondary" onClick={dismiss}>Schließen</Btn>
          </div>
        </>
      )}
    </div>
  );
}
